import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { updatePassword } from '../../services/authService';
import { uploadAvatar } from '../../services/storageService';
import { supabase } from '../../lib/supabase';
import Sidebar from '../../components/navigation/Sidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import CropperModal from '../../components/CropperModal';
import CertificationEditor from '../../components/CertificationEditor';
import { ALL_SPECIALIZATIONS } from '../../utils/specializations';
import { normaliseCertifications, ensureAbsoluteUrl } from '../../utils/certifications';
import toast, { Toaster } from 'react-hot-toast';

const ROLE_LABELS = {
  client: 'Client',
  expert: 'Expert',
  admin: 'Administrator',
};

const ROLE_COLORS = {
  client: 'bg-primary/10 text-primary',
  expert: 'bg-secondary/10 text-secondary',
  admin: 'bg-error/10 text-error',
};

const AccountPage = () => {
  const navigate = useNavigate();
  const { user, userProfile, userRole, is2FAEnabled, refreshProfile, updateProfile } = useAuth();

  // ── Active section ─────────────────────────────────────────────
  const [activeSection, setActiveSection] = useState('profile');

  // ── Profile editing ────────────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  // ── Avatar ──────────────────────────────────────────────────
  const avatarInputRef = useRef(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null); // object URL for the cropper

  // ── Expert professional editing ──────────────────────────────────────
  const [expertProfile, setExpertProfile] = useState(null);
  const [expertEditing, setExpertEditing] = useState(false);
  const [expertLoading, setExpertLoading] = useState(false);
  const [expertForm, setExpertForm] = useState({
    title: '',
    bio: '',
    hourly_rate: '',
    years_experience: '',
    specialties: [],
    certifications: [],
  });
  const [specFilter, setSpecFilter] = useState('');

  // ── Expert work experiences ───────────────────────────────────────────
  const [workExperiences, setWorkExperiences] = useState([]);
  const [weAdding, setWeAdding] = useState(false);
  const [weSaving, setWeSaving] = useState(false);
  const [weDeleting, setWeDeleting] = useState(null); // id being deleted
  const EMPTY_WE = { company: '', role: '', start_year: '', end_year: '', description: '' };
  const [weForm, setWeForm] = useState(EMPTY_WE);

  // Load current avatar from user_profiles (clients) — experts get it from loadExpert below
  useEffect(() => {
    if (!user?.id || userRole === 'expert') return;
    supabase
      .from('user_profiles')
      .select('avatar_url')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data?.avatar_url) setAvatarUrl(data.avatar_url);
      });
  }, [user?.id, userRole]);

  // When user picks a file, open the cropper instead of uploading immediately
  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    setCropImageSrc(objectUrl);
    e.target.value = ''; // reset input so same file can be re-selected
  };

  // Called when the user confirms the crop
  const handleCropConfirm = async (blob) => {
    setCropImageSrc(null);
    if (!blob) return;
    setAvatarUploading(true);
    try {
      // Turn the blob into a File so uploadAvatar can process it
      const croppedFile = new File([blob], 'avatar.webp', { type: blob.type || 'image/webp' });
      const result = await uploadAvatar(user.id, croppedFile);
      if (!result.success) throw new Error(result.error);
      const url = result.url;
      setAvatarUrl(url);
      // Persist to user_profiles for all roles
      await supabase.from('user_profiles').update({ avatar_url: url }).eq('id', user.id);
      // Also persist to expert_profiles for marketplace display
      if (userRole === 'expert') {
        await supabase.from('expert_profiles').update({ avatar_url: url }).eq('user_id', user.id);
      }
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(err?.message || 'Failed to upload photo');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleCropCancel = () => {
    if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
    setCropImageSrc(null);
  };

  // Load expert profile data
  useEffect(() => {
    const loadExpert = async () => {
      if (!user?.id || userRole !== 'expert') return;
      const [{ data }, { data: weData }] = await Promise.all([
        supabase
          .from('expert_profiles')
          .select('title, bio, hourly_rate, years_experience, specialties, certifications, status, avatar_url')
          .eq('user_id', user.id)
          .single(),
        supabase
          .from('expert_work_experiences')
          .select('*')
          .eq('user_id', user.id)
          .order('start_year', { ascending: false }),
      ]);
      if (data) {
        const normalised = { ...data, certifications: normaliseCertifications(data.certifications) };
        setExpertProfile(normalised);
        if (data.avatar_url) setAvatarUrl(data.avatar_url);
        setExpertForm({
          title: data.title || '',
          bio: data.bio || '',
          hourly_rate: data.hourly_rate != null ? String(data.hourly_rate) : '',
          years_experience: data.years_experience != null ? String(data.years_experience) : '',
          specialties: data.specialties || [],
          certifications: normaliseCertifications(data.certifications),
        });
      }
      setWorkExperiences(weData || []);
    };
    loadExpert();
  }, [user?.id, userRole]);

  const handleExpertSave = async (e) => {
    e.preventDefault();
    setExpertLoading(true);
    try {
      const updates = {
        title: expertForm.title.trim() || null,
        bio: expertForm.bio.trim() || null,
        hourly_rate: expertForm.hourly_rate ? parseFloat(expertForm.hourly_rate) : null,
        years_experience: expertForm.years_experience ? parseInt(expertForm.years_experience, 10) : null,
        specialties: expertForm.specialties,
        certifications: expertForm.certifications,
      };
      const { error } = await supabase
        .from('expert_profiles')
        .update(updates)
        .eq('user_id', user.id);
      if (error) throw error;
      setExpertProfile({ ...expertProfile, ...updates });
      setExpertEditing(false);
      toast.success('Professional profile updated');
    } catch (err) {
      toast.error(err?.message || 'Failed to update professional profile');
    } finally {
      setExpertLoading(false);
    }
  };

  const handleExpertCancel = () => {
    setExpertForm({
      title: expertProfile?.title || '',
      bio: expertProfile?.bio || '',
      hourly_rate: expertProfile?.hourly_rate != null ? String(expertProfile.hourly_rate) : '',
      years_experience: expertProfile?.years_experience != null ? String(expertProfile.years_experience) : '',
      specialties: expertProfile?.specialties || [],
      certifications: normaliseCertifications(expertProfile?.certifications),
    });
    setSpecFilter('');
    setExpertEditing(false);
  };

  const handleAddWorkExp = async (e) => {
    e.preventDefault();
    if (!weForm.company.trim() || !weForm.role.trim() || !weForm.start_year) {
      toast.error('Company, role and start year are required');
      return;
    }
    setWeSaving(true);
    try {
      const payload = {
        user_id: user.id,
        company: weForm.company.trim(),
        role: weForm.role.trim(),
        start_year: parseInt(weForm.start_year, 10),
        end_year: weForm.end_year ? parseInt(weForm.end_year, 10) : null,
        description: weForm.description.trim() || null,
      };
      const { data, error } = await supabase
        .from('expert_work_experiences')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      setWorkExperiences(prev => [data, ...prev].sort((a, b) => b.start_year - a.start_year));
      setWeForm(EMPTY_WE);
      setWeAdding(false);
      toast.success('Work experience added');
    } catch (err) {
      toast.error(err?.message || 'Failed to add experience');
    } finally {
      setWeSaving(false);
    }
  };

  const handleDeleteWorkExp = async (id) => {
    setWeDeleting(id);
    try {
      const { error } = await supabase.from('expert_work_experiences').delete().eq('id', id);
      if (error) throw error;
      setWorkExperiences(prev => prev.filter(we => we.id !== id));
      toast.success('Entry removed');
    } catch (err) {
      toast.error(err?.message || 'Failed to remove entry');
    } finally {
      setWeDeleting(null);
    }
  };

  const toggleSpecialty = (s) => {
    setExpertForm(prev => ({
      ...prev,
      specialties: prev.specialties.includes(s)
        ? prev.specialties.filter(x => x !== s)
        : [...prev.specialties, s],
    }));
  };

  // Sync form values whenever userProfile loads
  useEffect(() => {
    setEditName(userProfile?.full_name || '');
    setEditEmail(user?.email || '');
  }, [userProfile?.full_name, user?.email]);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!editName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    setProfileLoading(true);
    try {
      // Update full_name in user_profiles
      const { error: profileError } = await updateProfile({ full_name: editName.trim() });
      if (profileError) throw profileError;

      // If email changed, update auth + profile email
      if (editEmail.trim() && editEmail.trim() !== user?.email) {
        const { error: emailError } = await supabase?.auth?.updateUser({ email: editEmail.trim() });
        if (emailError) throw emailError;
        // Optimistically update the profile row too (Supabase won't auto-sync until confirmed)
        await updateProfile({ email: editEmail.trim() });
        toast.success('Profile updated! Check your new email address to confirm the change.');
      } else {
        toast.success('Profile updated successfully');
      }

      setEditing(false);
    } catch (err) {
      toast.error(err?.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditName(userProfile?.full_name || '');
    setEditEmail(user?.email || '');
    setEditing(false);
  };

  // ── Password change ────────────────────────────────────────────
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // ── 2FA disable ────────────────────────────────────────────────
  const [disabling2FA, setDisabling2FA] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  // ── Handlers ───────────────────────────────────────────────────
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      const { error } = await updatePassword(newPassword);
      if (error) throw error;
      toast.success('Password updated successfully');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err?.message || 'Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handle2FADisable = async () => {
    setDisabling2FA(true);
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({ twofa_enabled: false, twofa_secret: null })
        ?.eq('id', user?.id);
      if (error) throw error;
      await refreshProfile();
      toast.success('Two-factor authentication has been disabled');
      setShowDisableConfirm(false);
    } catch (err) {
      toast.error(err?.message || 'Failed to disable 2FA');
    } finally {
      setDisabling2FA(false);
    }
  };

  const navSections = [
    { id: 'profile', label: 'Profile', icon: 'User' },
    { id: 'security', label: 'Security', icon: 'Shield' },
    ...(userRole === 'expert' ? [{ id: 'professional', label: 'Professional', icon: 'Briefcase' }] : []),
  ];

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-center" />
      <Sidebar userRole={userRole} />

      <div className="lg:ml-60 min-h-screen">
        {/* ── Header ─────────────────────────────────────────── */}
        <header className="sticky top-0 z-40 bg-card border-b border-border px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  const isCollapsed = document.body.classList.toggle('sidebar-collapsed');
                  localStorage.setItem('sidebarCollapsed', isCollapsed.toString());
                }}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                aria-label="Toggle sidebar"
              >
                <Icon name="PanelLeft" size={24} />
              </button>
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold text-foreground">Account Settings</h1>
                <p className="text-sm text-muted-foreground mt-1">Manage your profile and security preferences</p>
              </div>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="ArrowLeft" size={16} />
              Back
            </button>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-8">
          <div className="max-w-4xl mx-auto flex gap-8">

            {/* ── Left nav ──────────────────────────────────── */}
            <aside className="hidden md:block w-48 flex-shrink-0">
              <nav className="space-y-1">
                {navSections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setActiveSection(s.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                      activeSection === s.id
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={s.icon} size={18} />
                    {s.label}
                  </button>
                ))}
              </nav>
            </aside>

            {/* ── Mobile tab bar ────────────────────────────── */}
            <div className="md:hidden flex gap-2 mb-6 w-full">
              {navSections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActiveSection(s.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === s.id
                      ? 'bg-primary text-white'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  <Icon name={s.icon} size={16} />
                  {s.label}
                </button>
              ))}
            </div>

            {/* ── Content ───────────────────────────────────── */}
            <div className="flex-1 space-y-6">

              {/* ════════════════ PROFILE ════════════════ */}
              {activeSection === 'profile' && (
                <>
                  <div className="bg-card border border-border rounded-xl p-6">
                    {/* Header row */}
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-lg font-semibold text-foreground">Profile Information</h2>
                      {!editing && (
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Pencil"
                          iconPosition="left"
                          onClick={() => setEditing(true)}
                        >
                          Edit
                        </Button>
                      )}
                    </div>

                    {/* Avatar + identity */}
                    <div className="flex items-center gap-5 mb-8">
                      {/* Clickable avatar with upload overlay */}
                      <div className="relative group flex-shrink-0">
                        <input
                          ref={avatarInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={handleAvatarChange}
                          className="hidden"
                        />
                        {avatarUrl ? (
                          <Image
                            src={avatarUrl}
                            alt={userProfile?.full_name || 'Avatar'}
                            className="w-16 h-16 rounded-full object-cover ring-2 ring-border"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                            <Icon name="User" size={32} className="text-primary" />
                          </div>
                        )}
                        {/* Overlay */}
                        <button
                          type="button"
                          disabled={avatarUploading}
                          onClick={() => avatarInputRef.current?.click()}
                          className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          title="Change photo"
                        >
                          {avatarUploading
                            ? <Icon name="Loader2" className="w-5 h-5 text-white animate-spin" />
                            : <Icon name="Camera" className="w-5 h-5 text-white" />}
                        </button>
                      </div>
                      <div>
                        <p className="text-xl font-semibold text-foreground">
                          {userProfile?.full_name || 'User'}
                        </p>
                        <p className="text-sm text-muted-foreground">{user?.email}</p>
                        <span className={`inline-block mt-2 text-xs font-medium px-2.5 py-1 rounded-full ${ROLE_COLORS[userRole] || ROLE_COLORS.client}`}>
                          {ROLE_LABELS[userRole] || 'User'}
                        </span>
                      </div>
                    </div>

                    {/* Fields — edit mode or read-only */}
                    {editing ? (
                      <form onSubmit={handleProfileSave} className="space-y-5">
                        <div>
                          <Input
                            label="Full Name"
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="Your full name"
                            required
                          />
                        </div>
                        <div>
                          <Input
                            label="Email Address"
                            type="email"
                            value={editEmail}
                            onChange={(e) => setEditEmail(e.target.value)}
                            placeholder="your@email.com"
                            required
                          />
                          {editEmail !== user?.email && (
                            <p className="mt-1.5 text-xs text-warning flex items-center gap-1">
                              <Icon name="Info" size={12} />
                              You'll receive a confirmation email at the new address before it takes effect.
                            </p>
                          )}
                        </div>

                        {/* Read-only info rows */}
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Role</label>
                          <div className="flex items-center gap-3 px-4 py-2.5 bg-muted rounded-lg text-sm text-muted-foreground">
                            <Icon name="ShieldCheck" size={16} className="text-muted-foreground" />
                            {ROLE_LABELS[userRole] || 'User'}
                            <span className="ml-1 text-xs">(cannot be changed)</span>
                          </div>
                        </div>

                        <div className="flex gap-3 pt-1">
                          <Button
                            type="submit"
                            disabled={profileLoading}
                            iconName="Save"
                            iconPosition="left"
                            className="flex-1 sm:flex-none"
                          >
                            {profileLoading ? 'Saving…' : 'Save Changes'}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancelEdit}
                            disabled={profileLoading}
                            className="flex-1 sm:flex-none"
                          >
                            Cancel
                          </Button>
                        </div>
                      </form>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Full Name</label>
                          <div className="flex items-center gap-3 px-4 py-2.5 bg-muted rounded-lg text-sm text-foreground">
                            <Icon name="User" size={16} className="text-muted-foreground" />
                            {userProfile?.full_name || '—'}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Email Address</label>
                          <div className="flex items-center gap-3 px-4 py-2.5 bg-muted rounded-lg text-sm text-foreground">
                            <Icon name="Mail" size={16} className="text-muted-foreground" />
                            {user?.email}
                            {userProfile?.email_verified_at && (
                              <span className="ml-auto flex items-center gap-1 text-xs text-success font-medium">
                                <Icon name="CheckCircle" size={14} />
                                Verified
                              </span>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Role</label>
                          <div className="flex items-center gap-3 px-4 py-2.5 bg-muted rounded-lg text-sm text-foreground">
                            <Icon name="ShieldCheck" size={16} className="text-muted-foreground" />
                            {ROLE_LABELS[userRole] || 'User'}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Member Since</label>
                          <div className="flex items-center gap-3 px-4 py-2.5 bg-muted rounded-lg text-sm text-foreground">
                            <Icon name="Calendar" size={16} className="text-muted-foreground" />
                            {user?.created_at
                              ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                              : '—'}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ════════════════ SECURITY ════════════════ */}
              {activeSection === 'security' && (
                <>
                  {/* Two-Factor Authentication card */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${is2FAEnabled ? 'bg-success/10' : 'bg-warning/10'}`}>
                          <Icon
                            name={is2FAEnabled ? 'ShieldCheck' : 'ShieldAlert'}
                            size={20}
                            className={is2FAEnabled ? 'text-success' : 'text-warning'}
                          />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-foreground">Two-Factor Authentication</h2>
                          <p className="text-sm text-muted-foreground">
                            {is2FAEnabled
                              ? 'Your account is secured with an authenticator app.'
                              : 'Add a second layer of protection to your account.'}
                          </p>
                        </div>
                      </div>
                      <span className={`flex-shrink-0 text-xs font-semibold px-3 py-1 rounded-full ${is2FAEnabled ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}>
                        {is2FAEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>

                    <div className="mt-5 pt-5 border-t border-border">
                      {is2FAEnabled ? (
                        <div className="space-y-4">
                          {/* Info row */}
                          <div className="flex items-start gap-3 p-4 bg-success/5 border border-success/20 rounded-lg">
                            <Icon name="CheckCircle" size={18} className="text-success mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-foreground">
                              <p className="font-medium mb-0.5">2FA is active</p>
                              <p className="text-muted-foreground">
                                Every login requires a 6-digit code from your authenticator app (Google Authenticator, Authy, etc.).
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-col sm:flex-row gap-3">
                            <Button
                              variant="outline"
                              iconName="RefreshCw"
                              iconPosition="left"
                              onClick={() => navigate('/2fa-setup')}
                              className="flex-1"
                            >
                              Reconfigure 2FA
                            </Button>
                            <Button
                              variant="outline"
                              iconName="ShieldOff"
                              iconPosition="left"
                              onClick={() => setShowDisableConfirm(true)}
                              className="flex-1 text-error border-error/30 hover:bg-error/5"
                            >
                              Disable 2FA
                            </Button>
                          </div>

                          {/* Disable confirm dialog */}
                          {showDisableConfirm && (
                            <div className="p-4 border border-error/30 bg-error/5 rounded-lg space-y-3">
                              <div className="flex items-start gap-3">
                                <Icon name="AlertTriangle" size={18} className="text-error mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-sm font-semibold text-foreground">Disable two-factor authentication?</p>
                                  <p className="text-sm text-muted-foreground mt-0.5">
                                    This will remove the extra layer of security from your account. You'll only need your password to log in.
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setShowDisableConfirm(false)}
                                  className="flex-1"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={handle2FADisable}
                                  disabled={disabling2FA}
                                  className="flex-1 bg-error hover:bg-error/90 text-white border-0"
                                >
                                  {disabling2FA ? 'Disabling…' : 'Yes, Disable 2FA'}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* How it works */}
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {[
                              { icon: 'Download', title: 'Get an app', desc: 'Install Google Authenticator, Authy, or any TOTP app.' },
                              { icon: 'QrCode', title: 'Scan QR code', desc: 'Scan the QR code shown during setup with your app.' },
                              { icon: 'KeyRound', title: 'Enter the code', desc: 'Enter the 6-digit code from the app when you log in.' },
                            ].map((step) => (
                              <div key={step.title} className="flex flex-col items-center text-center p-4 bg-muted/50 rounded-lg">
                                <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center mb-2">
                                  <Icon name={step.icon} size={18} className="text-primary" />
                                </div>
                                <p className="text-sm font-medium text-foreground mb-0.5">{step.title}</p>
                                <p className="text-xs text-muted-foreground">{step.desc}</p>
                              </div>
                            ))}
                          </div>

                          <Button
                            iconName="ShieldPlus"
                            iconPosition="left"
                            onClick={() => navigate('/2fa-setup')}
                            className="w-full"
                          >
                            Enable Two-Factor Authentication
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Change Password card */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon name="Lock" size={20} className="text-primary" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Change Password</h2>
                        <p className="text-sm text-muted-foreground">Use a strong, unique password for your account.</p>
                      </div>
                    </div>

                    <form onSubmit={handlePasswordChange} className="space-y-4">
                      <div className="relative">
                        <Input
                          label="New Password"
                          type={showPasswords ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="At least 8 characters"
                          required
                        />
                      </div>
                      <div className="relative">
                        <Input
                          label="Confirm New Password"
                          type={showPasswords ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Repeat new password"
                          required
                        />
                      </div>
                      <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={showPasswords}
                          onChange={(e) => setShowPasswords(e.target.checked)}
                          className="rounded border-border"
                        />
                        Show passwords
                      </label>
                      <Button
                        type="submit"
                        disabled={passwordLoading}
                        iconName="Save"
                        iconPosition="left"
                        className="w-full sm:w-auto"
                      >
                        {passwordLoading ? 'Saving…' : 'Update Password'}
                      </Button>
                    </form>
                  </div>

                  {/* Active sessions info */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon name="Monitor" size={20} className="text-primary" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Current Session</h2>
                        <p className="text-sm text-muted-foreground">You are currently logged in via this browser session.</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg text-sm text-foreground">
                      <Icon name="Globe" size={16} className="text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">Web Browser</p>
                        <p className="text-xs text-muted-foreground truncate">{window.navigator?.userAgent?.split(' ').slice(-2).join(' ')}</p>
                      </div>
                      <span className="flex items-center gap-1 text-xs text-success font-medium flex-shrink-0">
                        <span className="w-2 h-2 bg-success rounded-full" />
                        Active
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* ════════════════ PROFESSIONAL (Expert only) ════════════════ */}
              {activeSection === 'professional' && userRole === 'expert' && (
                <>
                  <div className="bg-card border border-border rounded-xl p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
                          <Icon name="Briefcase" size={20} className="text-secondary" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-foreground">Professional Profile</h2>
                          <p className="text-sm text-muted-foreground">Visible on the expert marketplace</p>
                        </div>
                      </div>
                      {!expertEditing && (
                        <Button variant="outline" size="sm" iconName="Pencil" iconPosition="left" onClick={() => setExpertEditing(true)}>
                          Edit
                        </Button>
                      )}
                    </div>

                    {expertEditing ? (
                      <form onSubmit={handleExpertSave} className="space-y-5">
                        {/* Title */}
                        <Input
                          label="Professional Title"
                          type="text"
                          value={expertForm.title}
                          onChange={(e) => setExpertForm(p => ({ ...p, title: e.target.value }))}
                          placeholder="e.g. Senior Fire Investigator"
                        />

                        {/* Hourly Rate */}
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Hourly Rate ($)</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={expertForm.hourly_rate}
                              onChange={(e) => setExpertForm(p => ({ ...p, hourly_rate: e.target.value }))}
                              placeholder="0.00"
                              className="w-full pl-7 pr-3 py-2 rounded-lg border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 bg-transparent"
                            />
                          </div>
                        </div>

                        {/* Years of Experience */}
                        <Input
                          label="Years of Experience"
                          type="number"
                          min="0"
                          value={expertForm.years_experience}
                          onChange={(e) => setExpertForm(p => ({ ...p, years_experience: e.target.value }))}
                          placeholder="e.g. 10"
                        />

                        {/* Bio */}
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Professional Bio</label>
                          <textarea
                            value={expertForm.bio}
                            onChange={(e) => setExpertForm(p => ({ ...p, bio: e.target.value }))}
                            rows={4}
                            placeholder="Describe your experience, expertise, and what clients can expect…"
                            className="w-full px-3 py-2 rounded-lg border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 bg-transparent resize-none"
                          />
                        </div>

                        {/* Specialties picker */}
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Specializations</label>
                          {expertForm.specialties.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {expertForm.specialties.map(s => (
                                <span key={s} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                  {s}
                                  <button type="button" onClick={() => toggleSpecialty(s)} className="hover:text-error ml-0.5">
                                    <Icon name="X" className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="relative mb-1">
                            <Icon name="Search" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <input
                              type="text"
                              value={specFilter}
                              onChange={e => setSpecFilter(e.target.value)}
                              placeholder="Search specializations…"
                              className="w-full pl-9 pr-3 py-2 rounded-lg border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 bg-transparent"
                            />
                          </div>
                          <div className="max-h-40 overflow-y-auto rounded-lg border border-border divide-y divide-border">
                            {ALL_SPECIALIZATIONS
                              .filter(s => s.toLowerCase().includes(specFilter.toLowerCase()))
                              .map(s => {
                                const selected = expertForm.specialties.includes(s);
                                return (
                                  <button
                                    key={s}
                                    type="button"
                                    onClick={() => toggleSpecialty(s)}
                                    className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left transition-colors ${
                                      selected ? 'bg-primary/5 text-primary font-medium' : 'text-foreground hover:bg-muted'
                                    }`}
                                  >
                                    {s}
                                    {selected && <Icon name="Check" className="w-4 h-4" />}
                                  </button>
                                );
                              })}
                          </div>
                        </div>

                        {/* Certifications */}
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Certifications</label>
                          <CertificationEditor
                            certifications={expertForm.certifications}
                            onChange={(updated) => setExpertForm(p => ({ ...p, certifications: updated }))}
                            userId={user?.id}
                          />
                        </div>

                        {/* Save / Cancel */}
                        <div className="flex gap-3 pt-1">
                          <Button type="submit" disabled={expertLoading} iconName="Save" iconPosition="left" className="flex-1 sm:flex-none">
                            {expertLoading ? 'Saving…' : 'Save Changes'}
                          </Button>
                          <Button type="button" variant="outline" onClick={handleExpertCancel} disabled={expertLoading} className="flex-1 sm:flex-none">
                            Cancel
                          </Button>
                        </div>
                      </form>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Professional Title</label>
                          <div className="flex items-center gap-3 px-4 py-2.5 bg-muted rounded-lg text-sm text-foreground">
                            <Icon name="BadgeCheck" size={16} className="text-muted-foreground" />
                            {expertProfile?.title || '—'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Hourly Rate</label>
                          <div className="flex items-center gap-3 px-4 py-2.5 bg-muted rounded-lg text-sm text-foreground">
                            <Icon name="DollarSign" size={16} className="text-muted-foreground" />
                            {expertProfile?.hourly_rate != null ? `$${Number(expertProfile.hourly_rate).toFixed(2)}/hr` : '—'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Years of Experience</label>
                          <div className="flex items-center gap-3 px-4 py-2.5 bg-muted rounded-lg text-sm text-foreground">
                            <Icon name="Calendar" size={16} className="text-muted-foreground" />
                            {expertProfile?.years_experience != null ? `${expertProfile.years_experience} years` : '—'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Professional Bio</label>
                          <div className="px-4 py-2.5 bg-muted rounded-lg text-sm text-foreground whitespace-pre-wrap">
                            {expertProfile?.bio || '—'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Specializations</label>
                          {expertProfile?.specialties?.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                              {expertProfile.specialties.map(s => (
                                <span key={s} className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">{s}</span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">None added</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Certifications</label>
                          {expertProfile?.certifications?.length > 0 ? (
                            <div className="space-y-2">
                              {expertProfile.certifications.map((cert, i) => (
                                <div key={i} className="flex items-start gap-3 p-3 bg-muted/50 border border-border rounded-xl">
                                  {cert.image_url ? (
                                    <img
                                      src={cert.image_url}
                                      alt={cert.name}
                                      className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-border"
                                      onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling && (e.target.nextElementSibling.style.display = ''); }}
                                    />
                                  ) : null}
                                  {!cert.image_url ? (
                                    <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                                      <Icon name="Award" size={18} className="text-secondary" />
                                    </div>
                                  ) : (
                                    <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0" style={{ display: 'none' }}>
                                      <Icon name="Award" size={18} className="text-secondary" />
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-foreground">{cert.name}</p>
                                    {cert.description && <p className="text-xs text-muted-foreground mt-0.5">{cert.description}</p>}
                                    {cert.url && (
                                      <a href={ensureAbsoluteUrl(cert.url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1">
                                        <Icon name="ExternalLink" size={10} /> View credential
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">None added</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Work Experience card */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Icon name="Building2" size={20} className="text-primary" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-foreground">Work Experience</h2>
                          <p className="text-sm text-muted-foreground">Shown on your marketplace profile</p>
                        </div>
                      </div>
                      {!weAdding && (
                        <Button variant="outline" size="sm" iconName="Plus" iconPosition="left" onClick={() => setWeAdding(true)}>
                          Add Entry
                        </Button>
                      )}
                    </div>

                    {/* Add form */}
                    {weAdding && (
                      <form onSubmit={handleAddWorkExp} className="mb-6 p-4 border border-border rounded-xl bg-muted/30 space-y-3">
                        <h3 className="text-sm font-semibold text-foreground">New Entry</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-foreground mb-1">Company / Organisation <span className="text-error">*</span></label>
                            <input
                              type="text"
                              value={weForm.company}
                              onChange={e => setWeForm(p => ({ ...p, company: e.target.value }))}
                              placeholder="e.g. ABC Forensics Inc."
                              className="w-full px-3 py-2 rounded-lg border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 bg-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-foreground mb-1">Role / Position <span className="text-error">*</span></label>
                            <input
                              type="text"
                              value={weForm.role}
                              onChange={e => setWeForm(p => ({ ...p, role: e.target.value }))}
                              placeholder="e.g. Senior Fire Investigator"
                              className="w-full px-3 py-2 rounded-lg border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 bg-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-foreground mb-1">Start Year <span className="text-error">*</span></label>
                            <input
                              type="number"
                              min="1950"
                              max={new Date().getFullYear()}
                              value={weForm.start_year}
                              onChange={e => setWeForm(p => ({ ...p, start_year: e.target.value }))}
                              placeholder="e.g. 2015"
                              className="w-full px-3 py-2 rounded-lg border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 bg-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-foreground mb-1">End Year <span className="text-xs text-muted-foreground font-normal">(leave blank if current)</span></label>
                            <input
                              type="number"
                              min="1950"
                              max={new Date().getFullYear()}
                              value={weForm.end_year}
                              onChange={e => setWeForm(p => ({ ...p, end_year: e.target.value }))}
                              placeholder="e.g. 2022"
                              className="w-full px-3 py-2 rounded-lg border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 bg-transparent"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground mb-1">Description <span className="text-xs text-muted-foreground font-normal">(optional)</span></label>
                          <textarea
                            value={weForm.description}
                            onChange={e => setWeForm(p => ({ ...p, description: e.target.value }))}
                            rows={2}
                            placeholder="Brief summary of responsibilities or achievements…"
                            className="w-full px-3 py-2 rounded-lg border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 bg-transparent resize-none"
                          />
                        </div>
                        <div className="flex gap-2 pt-1">
                          <Button type="submit" size="sm" disabled={weSaving} iconName="Plus" iconPosition="left">
                            {weSaving ? 'Saving…' : 'Add'}
                          </Button>
                          <Button type="button" variant="outline" size="sm" onClick={() => { setWeAdding(false); setWeForm(EMPTY_WE); }}>
                            Cancel
                          </Button>
                        </div>
                      </form>
                    )}

                    {/* Table */}
                    {workExperiences.length > 0 ? (
                      <div className="overflow-x-auto rounded-xl border border-border">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-muted/50 border-b border-border">
                              <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Role</th>
                              <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Company</th>
                              <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">Period</th>
                              <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide hidden md:table-cell">Description</th>
                              <th className="px-4 py-2.5" />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {workExperiences.map(we => (
                              <tr key={we.id} className="hover:bg-muted/30 transition-colors">
                                <td className="px-4 py-3 font-medium text-foreground">{we.role}</td>
                                <td className="px-4 py-3 text-muted-foreground">{we.company}</td>
                                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                                  {we.start_year}–{we.end_year ?? 'Present'}
                                </td>
                                <td className="px-4 py-3 text-muted-foreground hidden md:table-cell max-w-xs truncate">
                                  {we.description || '—'}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <button
                                    onClick={() => handleDeleteWorkExp(we.id)}
                                    disabled={weDeleting === we.id}
                                    className="p-1.5 rounded-lg text-muted-foreground hover:text-error hover:bg-error/10 transition-colors disabled:opacity-50"
                                    title="Remove"
                                  >
                                    <Icon name={weDeleting === we.id ? 'Loader2' : 'Trash2'} size={15} className={weDeleting === we.id ? 'animate-spin' : ''} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      !weAdding && (
                        <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
                          <Icon name="Building2" size={32} className="mb-2 opacity-30" />
                          <p className="text-sm">No work experience added yet.</p>
                          <p className="text-xs mt-0.5">Add your employment history to strengthen your profile.</p>
                        </div>
                      )
                    )}
                  </div>

                  {/* Marketplace status */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        expertProfile?.status === 'approved' ? 'bg-success/10' : 'bg-warning/10'
                      }`}>
                        <Icon
                          name={expertProfile?.status === 'approved' ? 'CheckCircle' : 'Clock'}
                          size={20}
                          className={expertProfile?.status === 'approved' ? 'text-success' : 'text-warning'}
                        />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Marketplace Status</h2>
                        <p className="text-sm text-muted-foreground">
                          {expertProfile?.status === 'approved'
                            ? 'Your profile is live and visible to clients on the marketplace.'
                            : 'Your profile is pending admin approval. You\'ll be notified once approved.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

            </div>
          </div>
        </main>
      </div>

      {/* ── Image Cropper Modal ──────────────────────────── */}
      {cropImageSrc && (
        <CropperModal
          imageSrc={cropImageSrc}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
};

export default AccountPage;
